export type CouponAppliesTo = 'ORDER_SUBTOTAL' | 'DELIVERY_FEE' | 'ORDER_TOTAL' | 'PRODUCT_SUBTOTAL';
export type CouponType = 'AMOUNT' | 'PERCENTAGE';

export type AppliedCoupon = {
  type: CouponType;
  amount: number;
  appliesTo: CouponAppliesTo;
  productId?: string | null;
};

export type CouponDiscountResult = {
  discountBase: number;
  discountAmount: number;
  subtotalDiscountAmount: number;
  deliveryDiscountAmount: number;
  productDiscountAmount: number;
};

export function ceilCouponDiscountToCents(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.ceil(Math.round(value * 100000) / 1000) / 100;
}

export function calculateCouponDiscount(params: {
  coupon: AppliedCoupon;
  subtotal: number;
  deliveryFee: number;
  productSubtotal?: number;
}): CouponDiscountResult {
  const { coupon, subtotal, deliveryFee, productSubtotal = 0 } = params;

  let discountBase: number;
  switch (coupon.appliesTo) {
    case 'ORDER_SUBTOTAL':
      discountBase = subtotal;
      break;
    case 'DELIVERY_FEE':
      discountBase = deliveryFee;
      break;
    case 'ORDER_TOTAL':
      discountBase = subtotal + deliveryFee;
      break;
    case 'PRODUCT_SUBTOTAL':
      discountBase = productSubtotal;
      break;
  }

  let discountAmount: number;
  if (coupon.type === 'PERCENTAGE') {
    discountAmount = (discountBase * coupon.amount) / 100;
  } else {
    discountAmount = coupon.amount;
  }

  discountAmount = Math.min(discountAmount, discountBase);
  discountAmount = Math.max(0, discountAmount);

  let subtotalDiscountAmount = 0;
  let deliveryDiscountAmount = 0;
  let productDiscountAmount = 0;

  if (coupon.appliesTo === 'ORDER_SUBTOTAL') {
    subtotalDiscountAmount = discountAmount;
  } else if (coupon.appliesTo === 'DELIVERY_FEE') {
    deliveryDiscountAmount = discountAmount;
  } else if (coupon.appliesTo === 'PRODUCT_SUBTOTAL') {
    productDiscountAmount = discountAmount;
    subtotalDiscountAmount = discountAmount;
  } else if (coupon.appliesTo === 'ORDER_TOTAL') {
    // Discount applies to the full total; split proportionally for record-keeping
    const total = subtotal + deliveryFee;
    if (total > 0) {
      subtotalDiscountAmount = (discountAmount * subtotal) / total;
      deliveryDiscountAmount = discountAmount - subtotalDiscountAmount;
    }
  }

  return {
    discountBase,
    discountAmount,
    subtotalDiscountAmount,
    deliveryDiscountAmount,
    productDiscountAmount,
  };
}
